import React, { useState, useEffect } from 'react';
import { getStaff, addStaff, updateStaff, deleteStaff, Staff } from '../services/staffService';
import { ROLES, isManagerialRole } from '../constants';
import { Trash2, MapPin, Building2, Filter } from 'lucide-react';
import { useStoreLocation } from '../contexts/LocationContext';
import { matchesStaffLocation } from '../lib/transactionUtils';

interface Props {
  tenantId: string;
  prefillPosId?: string | null;
  defaultLocationId?: string | null;
  onSuccess?: () => void;
}

export const StaffManagementForm: React.FC<Props> = ({ tenantId, prefillPosId, defaultLocationId, onSuccess }) => {
  const { activeLocation, activeLocationId, isConsolidated, availableLocations } = useStoreLocation();
  const nonConsolidatedLocations = availableLocations.filter(l => !l.isConsolidatedOption);

  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [filterLocationId, setFilterLocationId] = useState<string>('all');
  
  const [newStaff, setNewStaff] = useState({ 
    name: '', 
    email: '', 
    posId: prefillPosId || '', 
    role: ROLES[0],
    locationId: defaultLocationId || (activeLocationId !== 'all' && activeLocationId !== 'employee_personal' ? activeLocationId : (nonConsolidatedLocations[0]?.id || 'loc-downtown'))
  });

  useEffect(() => {
    if (prefillPosId) {
      setNewStaff(prev => ({ ...prev, posId: prefillPosId }));
    }
  }, [prefillPosId]);

  useEffect(() => {
    if (activeLocationId && activeLocationId !== 'all' && activeLocationId !== 'employee_personal') {
      setNewStaff(prev => ({ ...prev, locationId: activeLocationId }));
      setFilterLocationId(activeLocationId);
    } else {
      setFilterLocationId('all');
    }
  }, [activeLocationId]);

  useEffect(() => {
    const fetchData = async () => {
      const staffData = await getStaff(tenantId);
      setStaffList(staffData);
    };
    fetchData();
  }, [tenantId]);

  const [status, setStatus] = useState('');

  const handleAddStaff = async () => {
    if (newStaff.name) {
      try {
        const assignedLoc = nonConsolidatedLocations.find(l => l.id === newStaff.locationId);
        await addStaff(tenantId, { 
          tenantId, 
          ...newStaff, 
          locationId: newStaff.locationId,
          locationName: assignedLoc?.name,
          businessType: 'Restaurant' 
        });
        if (onSuccess) {
          onSuccess();
        } else {
          const data = await getStaff(tenantId);
          setStaffList(data);
          setNewStaff({ 
            name: '', 
            email: '', 
            posId: '', 
            role: ROLES[0],
            locationId: activeLocationId !== 'all' && activeLocationId !== 'employee_personal' ? activeLocationId : (nonConsolidatedLocations[0]?.id || 'loc-downtown')
          });
          setStatus('Staff added successfully');
        }
      } catch (error) {
        console.error('Error adding staff:', error);
        setStatus('Error adding staff. Please check console.');
      }
    }
  };

  const handleUpdateStaff = async (staff: Staff, field: keyof Staff, value: string) => {
    try {
      const updates: Partial<Staff> = { [field]: value };
      if (field === 'locationId') {
        const targetLoc = nonConsolidatedLocations.find(l => l.id === value);
        updates.locationName = targetLoc?.name || '';
      }
      await updateStaff(staff.tenantId, staff.id!, { ...staff, ...updates });
      const data = await getStaff(tenantId);
      setStaffList(data);
      setStatus('Staff updated successfully');
    } catch (error) {
      console.error('Error updating staff:', error);
      setStatus('Error updating staff. Please check console.');
    }
  };

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleDeleteStaff = async (staffId: string) => {
    try {
      await deleteStaff(tenantId, staffId);
      const data = await getStaff(tenantId);
      setStaffList(data);
      setStatus('Staff deleted successfully');
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Error deleting staff:', error);
      setStatus('Error deleting staff.');
      setDeleteConfirmId(null);
    }
  };

  // Filtered staff list based on location selection using compound rules
  const displayedStaff = staffList.filter(staff => {
    return matchesStaffLocation(staff, filterLocationId);
  });

  return (
    <div className="space-y-8 md:space-y-12 max-w-5xl mx-auto">
      <div className="border-b border-gray-50 pb-8">
        <h2 className="text-xl md:text-3xl font-black text-gray-900 tracking-tight uppercase font-sans mb-6">Staff Role Assignment</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="space-y-1">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Name</label>
            <input 
              placeholder="Full Name" 
              value={newStaff.name} 
              onChange={e => setNewStaff({...newStaff, name: e.target.value})} 
              className="input-field" 
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Email</label>
            <input 
              placeholder="Email Address" 
              value={newStaff.email} 
              onChange={e => setNewStaff({...newStaff, email: e.target.value})} 
              className="input-field" 
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">POSId</label>
            <input 
              placeholder="POSId" 
              value={newStaff.posId} 
              onChange={e => setNewStaff({...newStaff, posId: e.target.value})} 
              className="input-field" 
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Role</label>
            <select 
              value={newStaff.role} 
              onChange={e => setNewStaff({...newStaff, role: e.target.value})} 
              className="input-field"
            >
              <optgroup label="Management & Leadership">
                {ROLES.filter(r => isManagerialRole(r)).map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </optgroup>
              <optgroup label="Direct Service & Support Staff">
                {ROLES.filter(r => !isManagerialRole(r)).map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </optgroup>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Store Location</label>
            <select 
              value={newStaff.locationId} 
              onChange={e => setNewStaff({...newStaff, locationId: e.target.value})} 
              className="input-field"
            >
              {nonConsolidatedLocations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end sm:col-span-2 lg:col-span-5">
            <button onClick={handleAddStaff} className="btn-primary w-full">
              Add Staff Member
            </button>
          </div>
        </div>
        {status && <p className="p-4 text-sm font-medium text-blue-600">{status}</p>}
      </div>

      <div>
        <div className="py-4 md:py-6 border-b border-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg md:text-2xl font-black text-gray-900 tracking-tight uppercase font-sans">Staff Directory</h2>
            <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-2 py-1 rounded-lg uppercase tracking-widest">
              {displayedStaff.length} / {staffList.length} staff
            </span>
          </div>

          {/* Location Filter Dropdown */}
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-gray-400" />
            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Filter Store:</span>
            <select
              value={filterLocationId}
              onChange={(e) => setFilterLocationId(e.target.value)}
              className="text-xs font-bold bg-white border border-gray-200 rounded-xl px-3 py-1.5 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
            >
              <option value="all">All Locations (Consolidated)</option>
              {nonConsolidatedLocations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="md:hidden divide-y divide-gray-50 bg-white rounded-3xl overflow-hidden border border-gray-100">
          {displayedStaff.length > 0 ? displayedStaff.map((staff) => (
            <div key={staff.id} className="p-6 space-y-6">
              <div className="space-y-4">
                <div className="space-y-1">
                  <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Full Name</span>
                  <input 
                    defaultValue={staff.name} 
                    onBlur={(e) => handleUpdateStaff(staff, 'name', e.target.value)} 
                    className="w-full font-black text-lg text-gray-900 bg-gray-50 p-4 rounded-2xl border-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Email Address</span>
                  <input 
                    defaultValue={staff.email} 
                    onBlur={(e) => handleUpdateStaff(staff, 'email', e.target.value)} 
                    className="w-full text-sm font-bold text-gray-500 bg-gray-50 p-4 rounded-2xl border-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 pt-2">
                <div className="space-y-2">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Role Assignment</span>
                  <select 
                    defaultValue={staff.role} 
                    onChange={(e) => handleUpdateStaff(staff, 'role', e.target.value)} 
                    className={`w-full h-14 text-sm font-black uppercase tracking-widest rounded-2xl border-none py-1 px-4 focus:ring-2 focus:ring-blue-500 ${
                      isManagerialRole(staff.role) 
                        ? 'bg-purple-50 text-purple-700' 
                        : 'bg-blue-50 text-blue-700'
                    }`}
                  >
                    <optgroup label="Management & Leadership">
                      {ROLES.filter(r => isManagerialRole(r)).map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Direct Service & Support Staff">
                      {ROLES.filter(r => !isManagerialRole(r)).map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                <div className="space-y-2">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Store Location</span>
                  <select 
                    value={staff.locationId || 'loc-downtown'} 
                    onChange={(e) => handleUpdateStaff(staff, 'locationId', e.target.value)} 
                    className="w-full h-14 text-sm font-black uppercase tracking-widest rounded-2xl border-none py-1 px-4 focus:ring-2 focus:ring-blue-500 bg-slate-100 text-slate-800"
                  >
                    {nonConsolidatedLocations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">POS Bridge ID</span>
                  <input 
                    defaultValue={staff.posId} 
                    onBlur={(e) => handleUpdateStaff(staff, 'posId', e.target.value)} 
                    className="w-full h-14 text-sm font-bold text-gray-700 bg-gray-50 p-4 rounded-2xl border-none focus:ring-0" 
                    placeholder="Enter POS ID..."
                  />
                </div>
                <div className="flex justify-between items-center pt-4">
                  {deleteConfirmId === staff.id ? (
                    <div className="flex gap-4 w-full">
                      <button onClick={() => setDeleteConfirmId(null)} className="flex-1 py-4 text-xs font-black uppercase text-gray-500 bg-gray-100 rounded-2xl">Cancel</button>
                      <button onClick={() => handleDeleteStaff(staff.id!)} className="flex-1 py-4 text-xs font-black uppercase text-white bg-rose-600 rounded-2xl shadow-lg shadow-rose-600/20">Confirm Delete</button>
                    </div>
                  ) : (
                    <button 
                      onClick={() => setDeleteConfirmId(staff.id!)}
                      className="w-full py-4 text-rose-500 hover:text-rose-700 bg-rose-50 rounded-2xl flex items-center justify-center gap-2 text-xs font-black uppercase tracking-widest"
                      type="button"
                    >
                      <Trash2 size={16} />
                      Remove from Roster
                    </button>
                  )}
                </div>
              </div>
            </div>
          )) : (
            <div className="p-10 text-center text-gray-400 font-medium">No staff members found for this store selection</div>
          )}
        </div>

        {/* Desktop View: Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50">
                <th className="px-6 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Name</th>
                <th className="px-6 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Email</th>
                <th className="px-6 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">POSId</th>
                <th className="px-6 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Role</th>
                <th className="px-6 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Store Location</th>
                <th className="px-6 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {displayedStaff.map((staff) => (
                <tr key={staff.id} className="group hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <input 
                      defaultValue={staff.name} 
                      onBlur={(e) => handleUpdateStaff(staff, 'name', e.target.value)} 
                      className="w-full font-semibold text-gray-900 bg-transparent border-none p-0 focus:ring-0" 
                    />
                  </td>
                  <td className="px-6 py-4">
                    <input 
                      defaultValue={staff.email} 
                      onBlur={(e) => handleUpdateStaff(staff, 'email', e.target.value)} 
                      className="w-full text-gray-600 bg-transparent border-none p-0 focus:ring-0" 
                    />
                  </td>
                  <td className="px-6 py-4">
                    <input 
                      defaultValue={staff.posId} 
                      onBlur={(e) => handleUpdateStaff(staff, 'posId', e.target.value)} 
                      className="w-full text-gray-600 bg-transparent border-none p-0 focus:ring-0" 
                    />
                  </td>
                  <td className="px-6 py-4">
                    <select 
                      defaultValue={staff.role} 
                      onChange={(e) => handleUpdateStaff(staff, 'role', e.target.value)} 
                      className={`text-sm font-semibold rounded-lg border-none py-1 px-3 focus:ring-0 cursor-pointer ${
                        isManagerialRole(staff.role)
                          ? 'bg-purple-50 text-purple-700 font-bold'
                          : 'bg-blue-50 text-blue-700'
                      }`}
                    >
                      <optgroup label="Management & Leadership">
                        {ROLES.filter(r => isManagerialRole(r)).map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Direct Service & Support Staff">
                        {ROLES.filter(r => !isManagerialRole(r)).map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </optgroup>
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    <select 
                      value={staff.locationId || 'loc-downtown'} 
                      onChange={(e) => handleUpdateStaff(staff, 'locationId', e.target.value)} 
                      className="text-xs font-bold rounded-lg border border-gray-200 py-1.5 px-2.5 focus:ring-2 focus:ring-blue-500 bg-white text-gray-700 cursor-pointer"
                    >
                      {nonConsolidatedLocations.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end">
                      {deleteConfirmId === staff.id ? (
                        <div className="flex gap-2">
                          <button onClick={() => setDeleteConfirmId(null)} className="text-xs text-gray-500">Cancel</button>
                          <button onClick={() => handleDeleteStaff(staff.id!)} className="text-xs text-red-600 font-bold">Confirm</button>
                        </div>
                      ) : (
                        <button 
                          onClick={() => setDeleteConfirmId(staff.id!)}
                          className="text-red-500 hover:text-red-700 p-2 cursor-pointer"
                          type="button"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {displayedStaff.length === 0 && (
            <div className="p-8 text-center text-gray-400 font-medium">No staff members found for this store selection</div>
          )}
        </div>
      </div>
    </div>
  );
};
